import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useLyraContext } from '@/context/LyraContext'
import { useLocation } from 'react-router-dom'
import { X, Minus, Send, RotateCcw } from 'lucide-react'

const API_BASE = `${(import.meta as any).env?.VITE_API_URL || 'https://api.linguaproz.com'}/api/lyra`

function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split('\n')
  const nodes: React.ReactNode[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (!line.trim()) { nodes.push(<div key={i} className="h-2" />); i++; continue }
    // Numbered list item
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ''))
        i++
      }
      nodes.push(
        <ol key={i} className="list-decimal list-inside space-y-1 my-1">
          {items.map((item, j) => <li key={j}>{inlineFormat(item)}</li>)}
        </ol>
      )
      continue
    }
    // Bullet list item
    if (/^[-*]\s/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^[-*]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s/, ''))
        i++
      }
      nodes.push(
        <ul key={i} className="list-disc list-inside space-y-1 my-1">
          {items.map((item, j) => <li key={j}>{inlineFormat(item)}</li>)}
        </ul>
      )
      continue
    }
    nodes.push(<p key={i}>{inlineFormat(line)}</p>)
    i++
  }
  return nodes
}

function inlineFormat(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i}>{part.slice(2, -2)}</strong>
    if (part.startsWith('*') && part.endsWith('*'))
      return <em key={i}>{part.slice(1, -1)}</em>
    if (part.startsWith('`') && part.endsWith('`'))
      return <code key={i} className="bg-slate-100 text-slate-700 px-1 rounded text-xs font-mono">{part.slice(1, -1)}</code>
    return part
  })
}
// Parse [Segment N] → "text" apply patterns from AI responses
function parseApplyBlocks(text: string, onApply?: (order: number, text: string) => void): React.ReactNode {
  const applyRegex = /\[Segment (\d+)\]\s*→\s*"([^"]+)"/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = applyRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(...renderMarkdown(text.slice(lastIndex, match.index)))
    }
    const order = parseInt(match[1])
    const suggestion = match[2]
    parts.push(
      <div key={match.index} className="flex items-start gap-2 my-1.5 p-2 rounded-lg border border-gold-200 bg-amber-50">
        <div className="flex-1 min-w-0">
          <span className="text-xs font-semibold text-slate-500">Segment {order}</span>
          <p className="text-xs text-slate-800 mt-0.5 leading-relaxed">{suggestion}</p>
        </div>
        {onApply && (
          <button
            onClick={() => onApply(order, suggestion)}
            className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-900 transition-colors"
            style={{ background: '#E2B273' }}
          >
            Apply
          </button>
        )}
      </div>
    )
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) {
    parts.push(...renderMarkdown(text.slice(lastIndex)))
  }
  return parts.length > 0 ? <>{parts}</> : <>{renderMarkdown(text)}</>
}

const GREETED_KEY = 'lyra_greeted_session' 
const DISMISSED_KEY = 'lyra_dismissed'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

const QUICK_ACTIONS_GENERAL = [
  'How do I create a project?',
  'How does Translation Memory work?',
  'What file formats are supported?',
  'How do I import a glossary?',
  'What is the difference between Standard and Premium?',
]

const QUICK_ACTIONS_EDITOR = [
  'Suggest a translation',
  'Review this segment',
  'Check terminology',
  'Explain this term',
  'Check for register issues',
  'Suggest alternatives',
]

// Follow-up suggestions based on last assistant response topic
function getFollowUps(lastMessage: string, isEditor: boolean): string[] {
  if (!isEditor) return []
  const msg = lastMessage.toLowerCase()
  if (msg.includes('suggest') || msg.includes('translation') || msg.includes('segment')) {
    return ['Apply this suggestion', 'Suggest alternatives', 'Explain your reasoning']
  }
  if (msg.includes('terminolog') || msg.includes('term')) {
    return ['Add to glossary', 'Check other segments for this term', 'What is the pt-PT standard?']
  }
  if (msg.includes('register') || msg.includes('formal') || msg.includes('tone')) {
    return ['Rewrite more formally', 'Rewrite more concisely', 'Check next segment']
  }
  if (msg.includes('qa') || msg.includes('flag') || msg.includes('error')) {
    return ['Fix this issue', 'Explain in detail', 'Check all segments for this']
  }
  return ['Review next segment', 'Check terminology', 'Suggest alternatives']
}

// ── C3 Spark + constellation SVG avatar ─────────────────────────
function LyraAvatar({ size = 36, animate = false }: { size?: number; animate?: boolean }) {
  return (
    <span style={{ display: 'inline-block', position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="20" fill="#1e293b"/>
        <path d="M20 6 L22.8 17.2 L34 20 L22.8 22.8 L20 34 L17.2 22.8 L6 20 L17.2 17.2 Z" fill="#E2B273"/>
        <circle cx="20" cy="20" r="4" fill="#1e293b"/>
        <circle cx="31" cy="11" r="2.5" fill="#E2B273"
          style={{ opacity: animate ? 0 : 0.6, animation: animate ? 'lyra-pulse 1.8s ease-in-out 0s infinite' : 'none' }}/>
        <circle cx="9" cy="10" r="1.8" fill="#E2B273"
          style={{ opacity: animate ? 0 : 0.4, animation: animate ? 'lyra-pulse 1.8s ease-in-out 0.3s infinite' : 'none' }}/>
        <circle cx="30" cy="30" r="1.8" fill="#E2B273"
          style={{ opacity: animate ? 0 : 0.4, animation: animate ? 'lyra-pulse 1.8s ease-in-out 0.6s infinite' : 'none' }}/>
        <circle cx="10" cy="31" r="1.2" fill="#E2B273"
          style={{ opacity: animate ? 0 : 0.3, animation: animate ? 'lyra-pulse 1.8s ease-in-out 0.9s infinite' : 'none' }}/>
      </svg>
      <style>{`@keyframes lyra-pulse { 0%,100%{opacity:0} 50%{opacity:0.8} }`}</style>
    </span>
  )
}


export default function LyraBubble() {
  const { user } = useAuth()
  const { editorContext, lyraOpen, setLyraOpen, onApplySegmentEdit, lyraResetKey } = useLyraContext()
  const location = useLocation()
  const isEditor = location.pathname.includes('/editor/') || location.pathname.includes('/editor')

  const open = lyraOpen
  const setOpen = setLyraOpen
  const [minimised, setMinimised] = useState(false)
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISSED_KEY) === '1')
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [hasUnread, setHasUnread] = useState(false)
  const [consentGiven, setConsentGiven] = useState(() => !!localStorage.getItem('lyra_consent'))
  const [showConsent, setShowConsent] = useState(false)
  const [respondInTarget, setRespondInTarget] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => { scrollToBottom() }, [messages])

  // Reset messages and language toggle when editor unmounts
  useEffect(() => {
    if (lyraResetKey === 0) return
    setMessages([])
    setRespondInTarget(false)
  }, [lyraResetKey])

  // Auto-open on first login
  useEffect(() => {
    if (!user) return
    if (dismissed) return  // never auto-greet if dismissed
    const greeted = sessionStorage.getItem(GREETED_KEY)
    if (!greeted) {
      setTimeout(() => {
        setMessages([{
          role: 'assistant',
          content: `Hi ${user.name?.split(' ')[0] || 'there'}, I'm Lyra — your Translyx assistant. I can help you translate, manage terminology, and get the most out of TranslyxCAT. What would you like to do?`,
        }])
        setLyraOpen(true)
        sessionStorage.setItem(GREETED_KEY, '1')
      }, 2000)
    }
  }, [user, dismissed])

  // Show unread dot when minimised and new message arrives
  useEffect(() => {
    if (minimised && messages.length > 0 && messages[messages.length - 1].role === 'assistant') {
      setHasUnread(true)
    }
  }, [messages, minimised])

  const getToken = useCallback(async () => {
    const { supabase } = await import('@/lib/supabase')
    const { data: { session } } = await supabase.auth.getSession()
    return session?.access_token || null
  }, [])

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || loading) return

    if (!consentGiven) {
      setShowConsent(true)
      return
    }

    const userMessage: Message = { role: 'user', content: text.trim() }
    const updatedMessages = [...messages, userMessage]
    setMessages(updatedMessages)
    setInput('')
    setLoading(true)

    try {
      const token = await getToken()
      const mode = isEditor ? 'editor' : 'general'
      const context = isEditor ? { ...editorContext, app_version: '1.0.0-beta' } : { app_version: '1.0.0-beta' }

      const res = await fetch(`${API_BASE}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: updatedMessages.slice(-10),
          mode,
          context: { ...context, respond_in_target: respondInTarget },
        }),
      })

      const data = await res.json()
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply || 'Sorry, I could not generate a response.' }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong. Please try again.' }])
    } finally {
      setLoading(false)
    }
  }, [messages, loading, consentGiven, isEditor, editorContext, getToken])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  const handleConsent = () => {
    localStorage.setItem('lyra_consent', '1')
    setConsentGiven(true)
    setShowConsent(false)
  }

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1')
    setDismissed(true)
    setLyraOpen(false)
  }

  const handleOpen = () => {
    setLyraOpen(true)
    setMinimised(false)
    setHasUnread(false)
  }

  const handleMinimise = () => {
    setMinimised(true)
    setLyraOpen(false)
  }

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: "Chat cleared. How can I help you?",
    }])
  }

  const quickActions = isEditor ? QUICK_ACTIONS_EDITOR : QUICK_ACTIONS_GENERAL

  if (!user) return null

  return (
    <>
      {/* Floating bubble — editor only */}
      {!open && isEditor && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
          style={{ background: '#1e293b', border: '2px solid rgba(226,178,115,0.3)' }}
          title="Ask Lyra"
        >
          <LyraAvatar size={38} />
          {hasUnread && (
            <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-gold-500 border-2 border-white flex items-center justify-center">
              <span className="text-slate-900 text-xs font-black leading-none">!</span>
            </span>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed top-0 right-0 z-40 flex flex-col shadow-2xl"
          style={{
            width: '380px',
            height: '100vh',
            background: '#ffffff',
            borderLeft: '1px solid #e2e8f0',
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 px-3.5 py-3 shrink-0" style={{ background: '#1e293b' }}>
            <LyraAvatar size={30} animate={loading} />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold leading-tight">Lyra</p>
              <p className="text-xs leading-tight" style={{ color: '#E2B273' }}>
                {isEditor ? 'Translation Assistant' : 'Translyx Assistant'}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {isEditor && editorContext.language_pair && (
                <button
                  onClick={() => setRespondInTarget(t => !t)}
                  title={respondInTarget ? "Responding in target language" : "Responding in English"}
                  className={"px-2 py-1 rounded text-xs font-semibold transition-colors " + (
                    respondInTarget
                      ? "bg-gold-500 text-slate-900"
                      : "text-slate-400 hover:text-white bg-slate-700"
                  )}
                >
                  {respondInTarget
                    ? (editorContext.language_pair?.split(' -> ')[1] || 'TGT').toUpperCase().slice(0,5)
                    : 'EN'}
                </button>
              )}
              <button onClick={clearChat} title="Clear chat"
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
              <button onClick={handleMinimise} title="Minimise"
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
                <Minus className="w-3.5 h-3.5" />
              </button>
              <button onClick={handleDismiss} title="Dismiss"
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Consent overlay */}
          {showConsent && (
            <div className="absolute inset-0 z-10 flex items-end" style={{ background: 'rgba(15,23,42,0.6)' }}>
              <div className="bg-white w-full p-4 rounded-t-2xl">
                <p className="text-slate-900 text-sm font-semibold mb-2">Before we chat</p>
                <p className="text-slate-500 text-xs leading-relaxed mb-4">
                  Lyra uses AI to assist you. Messages and active segment content may be processed by our AI partners.
                  Do not share passwords or personal data.
                </p>
                <div className="flex gap-2">
                  <button onClick={handleConsent}
                    className="flex-1 py-2 rounded-xl text-sm font-bold text-slate-900 transition-colors"
                    style={{ background: '#E2B273' }}>
                    Got it, let's go
                  </button>
                  <button onClick={() => setShowConsent(false)}
                    className="px-4 py-2 rounded-xl text-sm text-slate-500 hover:bg-slate-100 transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: '#f8fafc' }}>
            {messages.length === 0 && (
              <div className="text-center pt-6">
                <LyraAvatar size={44} />
                <p className="text-slate-500 text-xs mt-3">Ask me anything about Translyx or your translation work.</p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
                {msg.role === 'assistant' && (
                  <div className="shrink-0 mt-0.5"><LyraAvatar size={22} /></div>
                )}
                <div
                  className="rounded-2xl px-3 py-2 text-xs leading-relaxed space-y-1"
                  style={{
                    maxWidth: msg.role === 'user' ? '260px' : '100%',
                    background: msg.role === 'user' ? '#1e293b' : '#ffffff',
                    color: msg.role === 'user' ? '#ffffff' : '#1e293b',
                    border: msg.role === 'assistant' ? '1px solid #e2e8f0' : 'none',
                    borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  }}
                >
                  {msg.role === 'assistant' ? (isEditor ? parseApplyBlocks(msg.content, onApplySegmentEdit) : renderMarkdown(msg.content)) : msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start gap-2">
                <div className="shrink-0 mt-0.5"><LyraAvatar size={22} animate={true} /></div>
                <div className="rounded-2xl px-3 py-2.5 bg-white border border-slate-200 flex gap-1 items-center"
                  style={{ borderRadius: '16px 16px 16px 4px' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-bounce" style={{ animationDelay: '0ms' }}/>
                  <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-bounce" style={{ animationDelay: '150ms' }}/>
                  <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-bounce" style={{ animationDelay: '300ms' }}/>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Follow-up suggestions after last assistant response */}
          {messages.length > 1 && !loading && isEditor && (() => {
            const lastMsg = messages[messages.length - 1]
            if (lastMsg.role !== 'assistant') return null
            const followUps = getFollowUps(lastMsg.content, isEditor)
            if (!followUps.length) return null
            return (
              <div className="px-3 pb-2 flex gap-1.5 flex-wrap shrink-0 bg-white border-t border-slate-100 pt-2">
                {followUps.map(action => (
                  <button key={action} onClick={() => sendMessage(action)}
                    className="text-xs px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-gold-400 hover:text-gold-600 transition-colors bg-white">
                    {action}
                  </button>
                ))}
              </div>
            )
          })()}
          {/* Quick actions — initial state */}
          {messages.length <= 1 && !loading && (
            <div className="px-3 pb-2 flex gap-1.5 flex-wrap shrink-0 bg-white border-t border-slate-100 pt-2">
              {quickActions.map(action => (
                <button key={action} onClick={() => sendMessage(action)}
                  className="text-xs px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-gold-400 hover:text-gold-600 transition-colors bg-white">
                  {action}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="flex items-end gap-2 px-3 py-2.5 border-t border-slate-200 bg-white shrink-0">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Lyra..."
              rows={1}
              className="flex-1 resize-none text-xs text-slate-800 placeholder-slate-400 border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30 transition-all"
              style={{ maxHeight: '80px' }}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading}
              className="w-8 h-8 rounded-xl flex items-center justify-center transition-colors disabled:opacity-40 shrink-0"
              style={{ background: '#E2B273' }}
            >
              <Send className="w-3.5 h-3.5 text-slate-900" />
            </button>
          </div>
        </div>
      )}
    </>
  )
}
